import React from 'react';
import { motion } from 'framer-motion';

// Reusable section component
const FeatureSection = ({ title, children, bgColor = 'bg-white' }) => (
  <section className={`py-16 ${bgColor}`}>
    <div className="container mx-auto">
      <h2 className="text-3xl font-bold mb-8">{title}</h2>
      <div className="prose prose-lg max-w-none">
        {children}
      </div>
    </div>
  </section>
);

// Icon for bullet points
const BulletIcon = () => (
  <svg className="inline-block w-5 h-5 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const FeaturePageTemplate = ({ 
  title, 
  description, 
  icon,
  benefits = [],
  details = [],
  useCases = [],
  additionalContent = null
}) => {
  return (
    <main className="pt-24 pb-16">
      {/* Hero section */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-20">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:w-1/2"
            >
              <div className="bg-purple-100 p-4 rounded-lg w-20 h-20 flex items-center justify-center text-primary mb-6">
                {icon}
              </div>
              <h1 className="text-4xl font-bold mb-6">{title}</h1>
              <p className="text-xl text-gray-700 mb-8">{description}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:w-1/2"
            >
              <div className="relative bg-white shadow-xl rounded-xl p-8 border border-purple-100">
                <h3 className="text-xl font-bold mb-4">Key Benefits</h3>
                <ul className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <BulletIcon />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Details section */}
      <FeatureSection title="How It Works" bgColor="bg-white">
        <div className="space-y-6">
          {details.map((detail, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-100">
              <h3 className="text-xl font-bold mb-2">{detail.title}</h3>
              <p>{detail.description}</p>
              {detail.items && (
                <ul className="mt-4 space-y-2">
                  {detail.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <BulletIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </FeatureSection>

      {/* Use cases section */}
      {useCases.length > 0 && (
        <FeatureSection title="Use Cases" bgColor="bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3">{useCase.title}</h3>
                <p className="text-gray-700">{useCase.description}</p>
              </div>
            ))}
          </div>
        </FeatureSection>
      )}

      {/* Additional content */}
      {additionalContent && (
        <FeatureSection title="Learn More" bgColor="bg-white">
          {additionalContent}
        </FeatureSection>
      )}

      {/* CTA section */}
      <section className="bg-purple-700 text-white py-16">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Data Quality?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            See how Qupid can help your team build trust in your data with comprehensive
            observability, monitoring, and quality assurance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#request-demo" className="button bg-white text-purple-700 hover:bg-gray-100">
              Request a Demo
            </a>
            <a href="/contact" className="button bg-transparent border-2 border-white hover:bg-white hover:text-purple-700">
              Contact Sales
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FeaturePageTemplate; 